import type { ReferralFees } from "@decibeltrade/sdk";

/** What the gold cell renders while commissions are not payable. */
export interface CommissionsBasis {
  /** Net fees less builder kickbacks — the figure a commission would come from. */
  basisUsd: number;
  netFeesUsd: number;
  builderPayoutsUsd: number;
  /** True when the window produced nothing, so the cell can say so plainly. */
  isEmpty: boolean;
}

/**
 * Reads the basis out of the fees response.
 *
 * `commission_basis_usd` is trusted from the server rather than recomputed here: it is
 * one subtraction, and doing it twice invites the two copies to drift.
 *
 * A negative basis is passed through. A network that collects more in maker rebates than
 * it pays in taker fees really has generated negative revenue, and clamping it to zero
 * would tell an affiliate their network broke even when it did not.
 */
export function toCommissionsBasis(fees: ReferralFees | undefined): CommissionsBasis {
  if (fees === undefined) {
    return { basisUsd: 0, netFeesUsd: 0, builderPayoutsUsd: 0, isEmpty: true };
  }

  return {
    basisUsd: fees.commission_basis_usd,
    netFeesUsd: fees.net_fees_usd,
    builderPayoutsUsd: fees.builder_payouts_usd,
    // Only a network that generated nothing at all is "empty". A basis of exactly zero
    // with fees on both sides is a real result and must still render its figures.
    isEmpty: fees.net_fees_usd === 0 && fees.builder_payouts_usd === 0,
  };
}
